import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Shield, Eye, EyeOff, Loader2, UserPlus, LogIn, Key, AlertTriangle } from 'lucide-react';
import api from '../lib/api';

const REGISTRATION_ROLES = [
  { value: 'admin', label: 'Amministratore', description: 'Gestione completa sicurezza' },
  { value: 'collaborator', label: 'Azienda Cliente', description: 'Accesso alle UL assegnate' },
  { value: 'gerente', label: 'Gerente UL', description: 'Gestione di una singola UL' },
  { value: 'medico_competente', label: 'Medico Competente', description: 'Gestione visite mediche' },
  { value: 'docente', label: 'Docente (interno)', description: 'Gestione formazione interna' },
  { value: 'docente_online', label: 'Docente Online', description: 'Formatore certificato — aule virtuali e registri pubblici (paga a chiave)' },
  { value: 'formatore', label: 'Content Creator', description: 'Crea materiale didattico, test e template' },
  { value: 'studio_tecnico', label: 'Studio Tecnico', description: 'Commesse, fatture, scadenze clienti' },
  { value: 'consulente_haccp', label: 'Consulente HACCP', description: 'Gestione laboratori e sampling' },
  { value: 'consulente_sicurezza', label: 'Consulente Sicurezza', description: 'Gestione UL e documenti sicurezza' },
];

const LICENSE_PLANS = [
  { value: 'mensile', label: 'Mensile', description: '30 giorni - €49' },
  { value: 'annuale', label: 'Annuale', description: '365 giorni - €490' },
];

const CENLogo = () => (
  <img 
    src="/cen_logo.png" 
    alt="CEN Engineering" 
    className="w-14 h-14 object-contain"
  />
);

export default function LoginPage() {
  const [isLogin, setIsLogin] = useState(true);
  const [showLicenseActivation, setShowLicenseActivation] = useState(false);
  const [renewalRequested, setRenewalRequested] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [requestedRole, setRequestedRole] = useState('admin');
  const [requestedPlan, setRequestedPlan] = useState('mensile');
  const [licenseKey, setLicenseKey] = useState('');
  const [message, setMessage] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleRequestRenewal = async () => {
    if (!email || !password) {
      toast.error('Inserisci email e password per richiedere il rinnovo');
      return;
    }
    setLoading(true);
    try {
      // First verify credentials
      const loginResponse = await api.post('/auth/login', { email, password, license_key: null });
      // This will fail with LICENSE_EXPIRED but we just need to verify credentials
    } catch (error) {
      if (error.response?.data?.detail === 'LICENSE_EXPIRED' || error.response?.data?.detail === 'LICENSE_REQUIRED') {
        // Credentials are valid, send renewal request
        try {
          await api.post(`/auth/request-renewal-anonymous?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}`);
          setRenewalRequested(true);
          toast.success('Richiesta di rinnovo inviata! Il Super Admin riceverà una notifica.');
        } catch (renewError) {
          toast.error(renewError.response?.data?.detail || 'Errore nell\'invio della richiesta');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Credenziali non valide');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (showLicenseActivation) {
        // License activation
        const response = await api.post(`/auth/activate-license?email=${encodeURIComponent(email)}&password=${encodeURIComponent(password)}&license_key=${encodeURIComponent(licenseKey)}`);
        localStorage.setItem('token', response.data.token);
        toast.success('Licenza attivata con successo!');
        window.location.href = '/';
      } else if (isLogin) {
        try {
          await login(email, password);
          toast.success('Accesso effettuato con successo');
          navigate('/');
        } catch (error) {
          // Check if license expired
          if (error.response?.data?.detail === 'LICENSE_EXPIRED' || error.response?.data?.detail === 'LICENSE_REQUIRED') {
            setShowLicenseActivation(true);
            toast.error('Licenza scaduta o mancante. Inserisci una nuova chiave di licenza.');
          } else {
            throw error;
          }
        }
      } else {
        // Registration request
        await api.post('/auth/register', {
          email,
          password,
          full_name: fullName,
          requested_role: requestedRole,
          requested_plan: requestedPlan,
          message
        });
        toast.success('Richiesta di registrazione inviata! Attendi l\'approvazione dell\'amministratore.');
        setIsLogin(true);
        setEmail('');
        setPassword('');
        setFullName('');
        setRequestedRole('admin');
        setRequestedPlan('mensile');
        setMessage('');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Si è verificato un errore';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="flex items-center gap-4 mb-8">
            <CENLogo />
            <div>
              <h1 className="font-heading font-black text-2xl text-[#1a365d]">CEN Engineering</h1>
              <p className="text-xs text-muted-foreground uppercase tracking-widest">Safety Management Portal</p>
            </div>
          </div>

          <h2 className="font-heading font-bold text-3xl mb-2">
            {showLicenseActivation ? 'Attiva Licenza' : isLogin ? 'Bentornato' : 'Richiedi Accesso'}
          </h2>
          <p className="text-muted-foreground mb-8">
            {showLicenseActivation 
              ? 'Inserisci la chiave di licenza per riattivare il tuo account'
              : isLogin 
                ? 'Accedi per gestire la sicurezza delle tue Unità Locali' 
                : 'Compila il modulo per richiedere l\'accesso al sistema'
            }
          </p>

          {showLicenseActivation && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 text-amber-800">
                <AlertTriangle className="w-5 h-5" />
                <span className="font-semibold">Licenza Scaduta</span>
              </div>
              <p className="text-sm text-amber-700 mt-1">
                La tua licenza è scaduta. Hai due opzioni:
              </p>
              <ol className="text-sm text-amber-700 mt-2 list-decimal list-inside space-y-1">
                <li>Se hai già una chiave di licenza, inseriscila qui sotto</li>
                <li>Se non hai una chiave, clicca "Richiedi Rinnovo" per inviare una richiesta al Super Admin</li>
              </ol>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {!isLogin && !showLicenseActivation && (
              <div className="space-y-2">
                <Label htmlFor="fullName">Nome Completo *</Label>
                <Input
                  id="fullName"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Mario Rossi"
                  className="form-input"
                  required={!isLogin}
                  data-testid="register-name-input"
                />
              </div>
            )}

            {!isLogin && !showLicenseActivation && (
              <div className="space-y-2">
                <Label htmlFor="role">Ruolo Richiesto *</Label>
                <Select value={requestedRole} onValueChange={setRequestedRole}>
                  <SelectTrigger data-testid="register-role-select">
                    <SelectValue placeholder="Seleziona ruolo" />
                  </SelectTrigger>
                  <SelectContent>
                    {REGISTRATION_ROLES.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex flex-col">
                          <span>{role.label}</span>
                          <span className="text-xs text-muted-foreground">{role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {!isLogin && !showLicenseActivation && requestedRole === 'admin' && (
              <div className="space-y-2">
                <Label htmlFor="plan">Piano Licenza *</Label>
                <Select value={requestedPlan} onValueChange={setRequestedPlan}>
                  <SelectTrigger data-testid="register-plan-select">
                    <SelectValue placeholder="Seleziona piano" />
                  </SelectTrigger>
                  <SelectContent>
                    {LICENSE_PLANS.map((plan) => (
                      <SelectItem key={plan.value} value={plan.value}>
                        <div className="flex flex-col">
                          <span>{plan.label}</span>
                          <span className="text-xs text-muted-foreground">{plan.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email *</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email@esempio.it"
                className="form-input"
                required
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password *</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="form-input pr-10"
                  required
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  data-testid="toggle-password-btn"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {!isLogin && !showLicenseActivation && (
              <div className="space-y-2">
                <Label htmlFor="message">Messaggio (opzionale)</Label>
                <Textarea
                  id="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Indica il motivo della richiesta o informazioni aggiuntive..."
                  className="form-input min-h-[80px]"
                  data-testid="register-message-input"
                />
              </div>
            )}

            {showLicenseActivation && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="licenseKey">Chiave di Licenza</Label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="licenseKey"
                      type="text"
                      value={licenseKey}
                      onChange={(e) => setLicenseKey(e.target.value.toUpperCase())}
                      placeholder="CEN-XXXX-XXXX-XXXXXX"
                      className="form-input pl-10 font-mono"
                      data-testid="license-key-input"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Inserisci la chiave di licenza fornita dal Super Admin
                  </p>
                </div>

                {renewalRequested ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-sm text-green-800">
                      ✅ Richiesta di rinnovo inviata! Il Super Admin riceverà una notifica e ti invierà una nuova chiave.
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground mb-2">Oppure</p>
                    <Button 
                      type="button"
                      variant="outline"
                      className="w-full"
                      onClick={handleRequestRenewal}
                      disabled={loading || !email || !password}
                      data-testid="request-renewal-btn"
                    >
                      <AlertTriangle className="w-4 h-4 mr-2" />
                      Richiedi Rinnovo Licenza
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      Invia una richiesta al Super Admin per ottenere una nuova chiave
                    </p>
                  </div>
                )}
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full h-12 font-semibold bg-[#1a365d] hover:bg-[#2563eb]" 
              disabled={loading}
              data-testid="submit-btn"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : showLicenseActivation ? (
                <>
                  <Key className="w-5 h-5 mr-2" />
                  Attiva Licenza
                </>
              ) : isLogin ? (
                <>
                  <LogIn className="w-5 h-5 mr-2" />
                  Accedi
                </>
              ) : (
                <>
                  <UserPlus className="w-5 h-5 mr-2" />
                  Invia Richiesta
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-center space-y-2">
            {showLicenseActivation ? (
              <button
                type="button"
                onClick={() => {
                  setShowLicenseActivation(false);
                  setLicenseKey('');
                }}
                className="text-sm text-[#2563eb] hover:underline"
                data-testid="back-to-login-btn"
              >
                Torna al login
              </button>
            ) : (
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setMessage('');
                }}
                className="text-sm text-[#2563eb] hover:underline"
                data-testid="toggle-mode-btn"
              >
                {isLogin ? 'Non hai un account? Richiedi accesso' : 'Hai già un account? Accedi'}
              </button>
            )}
          </div>

          {!isLogin && !showLicenseActivation && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-800">
                <strong>Nota:</strong> Dopo aver inviato la richiesta, un Super Admin dovrà approvarla e assegnarti una licenza prima che tu possa accedere al sistema.
              </p>
            </div>
          )}
        </motion.div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block flex-1 relative">
        <div className="absolute inset-0 bg-[#1a365d]/90" />
        <img
          src="https://images.pexels.com/photos/3862132/pexels-photo-3862132.jpeg"
          alt="Safety inspection"
          className="w-full h-full object-cover mix-blend-overlay"
        />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center text-white max-w-lg">
            <div className="mb-6">
              <img 
                src="/cen_logo.png" 
                alt="CEN Logo" 
                className="w-24 h-24 mx-auto mb-4 bg-white/10 rounded-lg p-2"
              />
            </div>
            <h2 className="font-heading font-black text-4xl mb-4">
              Gestione Sicurezza Professionale
            </h2>
            <p className="text-white/80 text-lg">
              Documenti, formazione, controlli RSPP e gestione docenti in un unico portale. 
              Scadenze sempre sotto controllo con CEN Engineering.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
